import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { MaskLines } from "@/components/motion/Reveal";
import { BUSINESS, IMAGES, TRUST } from "@/data/site";
import { openWhatsApp } from "@/lib/wa";

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.12]);

  const bookMsg = `Merhaba ${BUSINESS.name}, randevu oluşturmak istiyorum.`;

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-ivory pt-28 lg:pt-24"
      data-testid="hero-section"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-8 lg:gap-4 items-center min-h-[calc(100svh-7rem)]">
        {/* Text column */}
        <div className="lg:col-span-6 xl:col-span-6 order-2 lg:order-1 relative z-10">
          <motion.p
            className="eyebrow text-gold mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            data-testid="hero-eyebrow"
          >
            {BUSINESS.cityLabel}
          </motion.p>

          <h1
            className="font-serif text-espresso leading-[0.98] text-[3.1rem] sm:text-6xl lg:text-[4.6rem] xl:text-[5.4rem] font-medium"
            aria-label="Güzelliğiniz bir seçim değil, bir duruştur."
          >
            <span aria-hidden="true">
              <MaskLines
                lines={["Güzelliğiniz", "bir seçim değil,", "bir duruştur."]}
                lineClassName="italic"
                delay={0.25}
              />
            </span>
          </h1>

          <motion.p
            className="mt-8 max-w-lg text-espresso/70 text-base sm:text-lg leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9 }}
            data-testid="hero-subtext"
          >
            Saç tasarımından profesyonel cilt bakımına, gelin hazırlığından türban
            tasarımına kadar güzelliğinize özel, bütünsel bir deneyim.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
          >
            <button
              onClick={() => openWhatsApp(bookMsg)}
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-espresso px-8 py-4 text-ivory text-sm font-medium tracking-wide transition-transform duration-300 hover:scale-[1.03] active:scale-95"
            >
              <MessageCircle size={18} strokeWidth={1.6} />
              WhatsApp’tan Randevu Al
            </button>
            <a
              href="#hizmetler"
              data-testid="hero-services-cta"
              className="inline-flex items-center justify-center rounded-full border border-espresso/25 px-8 py-4 text-espresso text-sm font-medium tracking-wide transition-colors duration-300 hover:bg-espresso hover:text-ivory"
            >
              Hizmetleri Keşfet
            </a>
          </motion.div>

          <motion.ul
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            data-testid="hero-trust"
          >
            {TRUST.map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm text-espresso/75">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                {t}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Image column */}
        <div className="lg:col-span-6 order-1 lg:order-2 relative">
          <motion.div
            className="relative overflow-hidden rounded-t-[180px] rounded-b-[24px] max-h-[62vh] lg:max-h-[78vh] aspect-[3/4] lg:aspect-[4/5] ml-auto w-full max-w-md lg:max-w-none"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: [0.19, 1, 0.22, 1], delay: 0.2 }}
          >
            <motion.img
              src={IMAGES.heroPortrait}
              alt="Hülya Suna Beauty firma sahibi — salon portresi"
              className="h-full w-full object-cover object-[68%_35%]"
              style={{ y, scale }}
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-espresso/25 via-transparent to-transparent" />
          </motion.div>

          {/* floating caption chip */}
          <motion.div
            className="hidden lg:block absolute -left-6 bottom-12 bg-ivory/90 backdrop-blur-md border border-espresso/10 rounded-xl px-5 py-4 shadow-sm"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
          >
            <p className="eyebrow text-gold mb-1">Stüdyo</p>
            <p className="font-serif text-2xl text-espresso leading-tight">
              Güzellik • Kuaför<br />Türban Tasarım
            </p>
          </motion.div>
        </div>
      </div>

      {/* scroll indicator */}
      <motion.a
        href="#hizmetler"
        aria-label="Aşağı kaydır"
        data-testid="hero-scroll-indicator"
        className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-espresso/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
      >
        <span className="eyebrow text-[10px]">Keşfet</span>
        <motion.span
          animate={reduce ? {} : { y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ArrowDown size={18} strokeWidth={1.4} />
        </motion.span>
      </motion.a>
    </section>
  );
}
